package com.tovtalk.presentation.viewmodel.post

import com.tovtalk.domain.entity.Post
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.entity.toggleLike
import com.tovtalk.domain.usecase.post.LikePostUseCase
import com.tovtalk.domain.repository.PostRepository
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.presentation.state.UiState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.cancel
import com.tovtalk.presentation.ViewModel
import com.tovtalk.presentation.ScopedViewModel

/**
 * Reel Detail ViewModel - לטיפול בטעינת רילס בודד (Reels Only Architecture)
 */
class PostDetailViewModel(
    private val postRepository: PostRepository,
    private val userRepository: UserRepository,
    private val scope: CoroutineScope
) : ViewModel(), ScopedViewModel {

    /** Cancels the injected scope + all launched jobs when the screen leaves. */
    override fun dispose() {
        scope.cancel()
    }


    private val _currentUserIsReadOnly = MutableStateFlow(false)
    val currentUserIsReadOnly: StateFlow<Boolean> = _currentUserIsReadOnly.asStateFlow()
    
    private val _reelState = MutableStateFlow<UiState<Post>>(UiState.Idle)
    val postState: StateFlow<UiState<Post>> = _reelState.asStateFlow()

    // Shared like path (same as Home/Profile). Built locally from the already-injected
    // postRepository to avoid a DI change; LikePostUseCase is a thin wrapper.
    private val likePostUseCase = LikePostUseCase(postRepository)

    init {
        scope.launch {
            when (val r = userRepository.getCurrentUser()) {
                is DomainResult.Success -> _currentUserIsReadOnly.value = r.data.isReadOnly
                else -> {}
            }
        }
    }
    
    fun loadPost(postRef: String, initialPost: Post? = null) = loadReel(postRef, initialPost)

    fun loadReel(postRef: String, initialPost: Post? = null) {
        if (initialPost != null && initialPost.ref == postRef) {
            _reelState.value = UiState.Success(initialPost)
            return
        }
        scope.launch {
            _reelState.value = UiState.Loading

            when (val result = postRepository.getPost(postRef)) {
                is DomainResult.Success -> {
                    _reelState.value = UiState.Success(result.data)
                }
                is DomainResult.Error -> {
                    _reelState.value = if (result.isKashrutNotAllowed) {
                        UiState.KashrutBlocked(result.message)
                    } else {
                        UiState.Error(result.message)
                    }
                }
                is DomainResult.Loading -> {
                    _reelState.value = UiState.Loading
                }
            }
        }
    }
    
    fun likePost(postId: Int, isLiked: Boolean) = likeReel(postId, isLiked)
    
    fun likeReel(postId: Int, isLiked: Boolean) {
        scope.launch {
            if (likePostUseCase(postId, isLiked) is DomainResult.Success) {
                // Update local state via shared helper
                val currentState = _reelState.value
                if (currentState is UiState.Success && currentState.data.id == postId) {
                    _reelState.value = UiState.Success(currentState.data.toggleLike(isLiked))
                }
            }
        }
    }

    fun incrementCommentCount(postId: Int) {
        val currentState = _reelState.value
        if (currentState is UiState.Success && currentState.data.id == postId) {
            _reelState.value = UiState.Success(
                currentState.data.copy(stats = currentState.data.stats.copy(comments = currentState.data.stats.comments + 1))
            )
        }
    }
}
